import { setTimeToBeDefined } from '../../../ts-utils/date-utils'
import { gameTimeZone, isGameTimeTbd } from './gameDayBucket'
import {
  assignmentsForBucket,
  dedupeAssignments,
  OFFICIAL_BUCKET_TYPES,
  sameAssignments,
} from './officialBuckets'
import type { OfficialAssignment, OfficialBucket } from './officialBuckets'
import type { CivilDate, CivilTime, GameLike, TeamRef, VenueRef } from './types'
import {
  isCivilDate,
  isCivilTime,
  preserveDuration,
  resolveTimeZone,
  setVenueLocalDate,
  setVenueLocalTime,
  shiftByLocalDays,
  shiftCivilDate,
  shiftByMinutes,
  toVenueLocalParts,
  venueLocalToInstant,
} from './venueLocalDateTime'

export type BulkAction =
  | { kind: 'setLocalDate'; date: CivilDate }
  | { kind: 'setLocalTime'; time: CivilTime }
  | { kind: 'setTbd' }
  | { kind: 'shiftLocalDays'; days: number }
  | { kind: 'shiftMinutes'; minutes: number }
  | { kind: 'setVenue'; venueId: string; facilityId: string | null }
  | { kind: 'setFacility'; facilityId: string }
  | { kind: 'setGameNumber'; value: string }
  | { kind: 'setGameType'; gameTypeId: number | null }
  /*
   * The two sides carry only the team uid. Whether that team is a PLACEHOLDER
   * is looked up in `teamsById` at plan time rather than ridden along on the
   * action: the options map is the only authority on it, and a second copy
   * would be one more thing that can disagree with it.
   */
  | { kind: 'setHomeTeam'; teamId: string }
  | { kind: 'setVisitingTeam'; teamId: string }
  | {
      kind: 'setOfficials'
      bucket: OfficialBucket
      assignments: OfficialAssignment[]
    }

export type BulkSkipReason =
  | 'no-change'
  | 'missing-start'
  | 'nonexistent-local-time'
  | 'tbd-not-shiftable-by-minutes'
  | 'venue-required-for-facility'
  | 'facility-not-in-venue'
  | 'unknown-venue'
  | 'inactive-facility'
  | 'invalid-day-shift'
  | 'official-role-outside-bucket'
  | 'division-required-for-placeholder-team'

export interface GamePatch {
  starts_at?: string
  ends_at?: string | null
  venue_id?: string
  /** `null` clears the surface — a venue move with none picked. */
  facility_id?: string | null
  home_team_id?: string
  visiting_team_id?: string
  /**
   * Sent ONLY to pin the division a game already sits in.
   *
   * `GamesController::update` re-parents a game to the home team's division
   * whenever the home team changes and this key is absent — which is right for
   * a real team and impossible for a placeholder, since a placeholder belongs
   * to no division and the lookup comes back empty (a 400). Naming the current
   * division is what keeps the game where it is.
   */
  schedule_id?: string
  league_game_number?: string
  game_type_id?: number | null
  if_necessary_status?: boolean
  /**
   * The game's officials for the roles named in `officials_scope`, as the FULL
   * desired list — the server reconciles rather than appending.
   *
   * Scoped because Referees, Scorekeepers and Timekeepers are three independent
   * columns over one pivot: a Referees write must leave the same person's
   * Scorekeeper row alone, and a grid row that has gone stale must not be able
   * to wipe assignments it never knew about.
   */
  officials?: { id: string; official_type_id: number }[]
  officials_scope?: number[]
}

export interface PlannedPatch {
  gameId: string
  patch: GamePatch
  /** Set when the wall clock the admin picked repeats on a fall-back night. */
  ambiguousLocalTime?: boolean
}

export interface PlannedSkip {
  gameId: string
  reason: BulkSkipReason
}

export interface BulkPlan {
  patches: PlannedPatch[]
  skipped: PlannedSkip[]
}

export interface BulkPlanContext {
  /** Every venue the grid knows about, keyed by uid — the union source. */
  venuesById: Map<string, VenueRef>
  /**
   * Every team the grid can offer, keyed by uid.
   *
   * Required rather than optional on purpose: it is the only place
   * `is_placeholder` is knowable, and a plan built without it would send a
   * placeholder patch with no division and take a 400 for it.
   */
  teamsById: Map<string, TeamRef>
}

const skip = (gameId: string, reason: BulkSkipReason): PlannedSkip => ({
  gameId,
  reason,
})

const sameInstant = (a?: string | null, b?: string | null): boolean => {
  if (a == null || b == null) {
    return a == null && b == null
  }
  const left = Date.parse(a)
  const right = Date.parse(b)
  return Number.isFinite(left) && Number.isFinite(right) && left === right
}

/**
 * Builds the timestamp half of a patch.
 *
 * `ends_at` always travels with `starts_at` so the game keeps the absolute
 * duration it already had — the API stores whatever it is handed, so omitting
 * the end silently stretches or shrinks the booking.
 */
const timePatch = (game: GameLike, nextStart: string): GamePatch => {
  const nextEnd = preserveDuration(game.starts_at, game.ends_at, nextStart)
  return nextEnd === null
    ? { starts_at: nextStart }
    : { starts_at: nextStart, ends_at: nextEnd }
}

const planTimeChange = (
  game: GameLike,
  nextStart: string | null,
  resolution: 'ok' | 'ambiguous' | 'nonexistent'
): PlannedPatch | PlannedSkip => {
  if (nextStart === null || resolution === 'nonexistent') {
    return skip(game.id, 'nonexistent-local-time')
  }
  if (sameInstant(game.starts_at, nextStart)) {
    return skip(game.id, 'no-change')
  }
  return {
    gameId: game.id,
    patch: timePatch(game, nextStart),
    ...(resolution === 'ambiguous' ? { ambiguousLocalTime: true } : {}),
  }
}

const planForGame = (
  game: GameLike,
  action: BulkAction,
  context: BulkPlanContext
): PlannedPatch | PlannedSkip => {
  const timeZone = gameTimeZone(game)
  const hasStart =
    typeof game.starts_at === 'string' && game.starts_at.trim().length > 0

  switch (action.kind) {
    case 'setLocalDate': {
      if (!hasStart) {
        return skip(game.id, 'missing-start')
      }
      if (!isCivilDate(action.date)) {
        return skip(game.id, 'nonexistent-local-time')
      }
      //  A TBD game carries its intended day in a UTC sentinel, so moving its
      //  date means re-stamping the sentinel — not converting it to a real time.
      if (isGameTimeTbd(game)) {
        const next = setTimeToBeDefined(`${action.date}T00:00:00.000Z`)
        return sameInstant(game.starts_at, next)
          ? skip(game.id, 'no-change')
          : { gameId: game.id, patch: timePatch(game, next) }
      }
      const { iso, resolution } = setVenueLocalDate(
        game.starts_at,
        timeZone,
        action.date
      )
      return planTimeChange(game, iso, resolution)
    }

    case 'setLocalTime': {
      if (!hasStart) {
        return skip(game.id, 'missing-start')
      }
      if (!isCivilTime(action.time)) {
        return skip(game.id, 'nonexistent-local-time')
      }
      //  Giving a TBD game a real time promotes it out of the sentinel, onto the
      //  day the sentinel was holding.
      if (isGameTimeTbd(game)) {
        const sentinelDay = String(game.starts_at).slice(0, 10)
        const { iso, resolution } = venueLocalToInstant(
          sentinelDay,
          action.time,
          timeZone
        )
        return planTimeChange(game, iso, resolution)
      }
      const { iso, resolution } = setVenueLocalTime(
        game.starts_at,
        timeZone,
        action.time
      )
      return planTimeChange(game, iso, resolution)
    }

    case 'setTbd': {
      if (!hasStart) {
        return skip(game.id, 'missing-start')
      }
      /*
       * Before the parts read, not merely as an optimisation.
       *
       * Re-deriving the day of an already-TBD game reads 23:58:58Z through the
       * venue zone, which east of UTC is ALREADY the next local day — so each
       * toggle would walk the game forward another day. Nothing to do here
       * anyway: the game is the state being asked for.
       */
      if (isGameTimeTbd(game)) {
        return skip(game.id, 'no-change')
      }
      /*
       * Stamp the sentinel on the day the admin can SEE — the venue-local
       * civil date the Date column shows — not the raw UTC date, which is
       * already tomorrow for any evening game at a western venue. `gameDayKey`
       * reads the venue-local date for a timed game and the sentinel's UTC
       * date for a TBD one, so this is what keeps the row on its day.
       */
      const parts = toVenueLocalParts(game.starts_at, timeZone)
      if (!parts.isValid) {
        return skip(game.id, 'nonexistent-local-time')
      }
      const next = setTimeToBeDefined(`${parts.date}T00:00:00.000Z`)
      return { gameId: game.id, patch: timePatch(game, next) }
    }

    case 'shiftLocalDays': {
      if (!hasStart) {
        return skip(game.id, 'missing-start')
      }
      if (action.days === 0) {
        return skip(game.id, 'no-change')
      }
      //  A fractional shift is not a local-day shift at all: the civil date it
      //  produces is the truncated one, so the game moves a different number of
      //  days than the admin typed. Say so rather than silently rounding.
      if (!Number.isInteger(action.days)) {
        return skip(game.id, 'invalid-day-shift')
      }
      //  A TBD game's day lives in a UTC sentinel, so shifting it means moving
      //  the SENTINEL DATE and re-stamping 23:58:58Z. Shifting the instant
      //  through a venue zone destroys the marker — a Vancouver day added to
      //  2026-03-07T23:58:58Z lands on 22:58:58Z, and the game silently becomes
      //  a real 10:58pm booking.
      if (isGameTimeTbd(game)) {
        const sentinelDay = String(game.starts_at).slice(0, 10)
        const shiftedDay = shiftCivilDate(sentinelDay, action.days)
        if (shiftedDay === null) {
          return skip(game.id, 'missing-start')
        }
        const next = setTimeToBeDefined(`${shiftedDay}T00:00:00.000Z`)
        return sameInstant(game.starts_at, next)
          ? skip(game.id, 'no-change')
          : { gameId: game.id, patch: timePatch(game, next) }
      }

      //  Local-day arithmetic, not 1440 minutes — across a DST boundary those
      //  land an hour apart and the game misses its slot. The helper rebuilds
      //  and RE-VALIDATES the target wall clock, so a spring-forward gap comes
      //  back as `nonexistent` rather than sliding an hour forward unannounced.
      const { iso, resolution } = shiftByLocalDays(
        game.starts_at,
        timeZone,
        action.days
      )
      return planTimeChange(game, iso, resolution)
    }

    case 'shiftMinutes': {
      if (!hasStart) {
        return skip(game.id, 'missing-start')
      }
      if (action.minutes === 0) {
        return skip(game.id, 'no-change')
      }
      //  A TBD game has no time to move; nudging the sentinel would just break
      //  the 23:58:58 marker and turn it into a real (wrong) time.
      if (isGameTimeTbd(game)) {
        return skip(game.id, 'tbd-not-shiftable-by-minutes')
      }
      const next = shiftByMinutes(game.starts_at, action.minutes)
      return planTimeChange(game, next, 'ok')
    }

    case 'setVenue': {
      const venue = context.venuesById.get(action.venueId)
      if (venue === undefined) {
        return skip(game.id, 'unknown-venue')
      }

      const facilityId = action.facilityId === '' ? null : action.facilityId
      const facilities = venue.facilities ?? []

      if (facilityId !== null) {
        const target = facilities.find((facility) => facility.id === facilityId)
        if (target === undefined) {
          return skip(game.id, 'facility-not-in-venue')
        }
        //  The update endpoint refuses a move ONTO an inactive surface, so
        //  catching it here turns a 422 into a stated reason.
        if (target.is_active === false) {
          return skip(game.id, 'inactive-facility')
        }
      }

      const venueUnchanged = game.venue?.id === action.venueId
      const facilityUnchanged = (game.facility?.id ?? null) === facilityId
      if (venueUnchanged && facilityUnchanged) {
        return skip(game.id, 'no-change')
      }

      /*
       * Venue and facility always travel together, and surface is optional.
       * With no surface picked, `facility_id` is sent as an explicit null so the
       * move CLEARS the old one — omitting it would leave the game on a surface
       * belonging to the venue it just left.
       */
      const patch: GamePatch = {
        venue_id: action.venueId,
        facility_id: facilityId,
      }

      /*
       * A venue move is a move between CLOCKS as well as buildings. Keeping the
       * instant would turn a 7pm Vancouver game into a 10pm Toronto game — the
       * booking the admin made is a 7pm slot, and the ice at the new rink is
       * booked in the new rink's local time. So the wall clock is carried across
       * and the instant recomputed, with the absolute duration preserved.
       *
       * A TBD game is exempt: its sentinel is a UTC calendar date with no wall
       * clock to carry.
       */
      const targetZone = resolveTimeZone(venue.timezone)
      const currentZone = gameTimeZone(game)
      if (hasStart && !isGameTimeTbd(game) && targetZone !== currentZone) {
        const parts = toVenueLocalParts(game.starts_at, currentZone)
        const { iso, resolution } = venueLocalToInstant(
          parts.date,
          parts.time,
          targetZone
        )
        if (iso === null || resolution === 'nonexistent') {
          //  The same wall clock does not exist at the new venue that day.
          return skip(game.id, 'nonexistent-local-time')
        }
        patch.starts_at = iso
        const nextEnd = preserveDuration(game.starts_at, game.ends_at, iso)
        if (nextEnd !== null) {
          patch.ends_at = nextEnd
        }
      }

      return { gameId: game.id, patch }
    }

    case 'setFacility': {
      const venueId = game.venue?.id
      if (venueId == null || venueId === '') {
        return skip(game.id, 'venue-required-for-facility')
      }
      const venue = context.venuesById.get(venueId)
      if (venue === undefined) {
        return skip(game.id, 'unknown-venue')
      }
      const belongs = (venue.facilities ?? []).some(
        (facility) => facility.id === action.facilityId
      )
      if (!belongs) {
        return skip(game.id, 'facility-not-in-venue')
      }
      if (game.facility?.id === action.facilityId) {
        return skip(game.id, 'no-change')
      }
      return {
        gameId: game.id,
        patch: { venue_id: venueId, facility_id: action.facilityId },
      }
    }

    case 'setGameNumber': {
      const current = game.league_game_number ?? ''
      if (current === action.value) {
        return skip(game.id, 'no-change')
      }
      return { gameId: game.id, patch: { league_game_number: action.value } }
    }

    case 'setGameType': {
      const current =
        game.game_type_id == null ? null : Number(game.game_type_id)
      if (current === action.gameTypeId) {
        return skip(game.id, 'no-change')
      }
      return { gameId: game.id, patch: { game_type_id: action.gameTypeId } }
    }

    /*
     * Both sides share one branch: the only difference is which field the patch
     * names, and splitting them would duplicate the placeholder rule — the half
     * of this that is easy to get wrong.
     */
    case 'setHomeTeam':
    case 'setVisitingTeam': {
      const isHome = action.kind === 'setHomeTeam'
      const current =
        (isHome ? game.homeTeam?.id : game.visitingTeam?.id) ?? null
      if (current === action.teamId) {
        return skip(game.id, 'no-change')
      }

      const patch: GamePatch = isHome
        ? { home_team_id: action.teamId }
        : { visiting_team_id: action.teamId }

      /*
       * A placeholder belongs to no division, so the API cannot derive one and
       * answers a home-team change with a 400. Naming the division the game is
       * already in keeps it where it is.
       *
       * Pinned for BOTH sides even though only a home change re-parents: the
       * request is what the grid can promise, and a visiting-side pin is a
       * no-op rather than a special case to remember.
       */
      if (context.teamsById.get(action.teamId)?.is_placeholder === true) {
        const divisionId = game.schedule?.id ?? null
        if (divisionId === null || divisionId === '') {
          //  Nothing to pin, so the patch cannot be made valid. Saying so beats
          //  sending it and reporting the 400 back as a mystery.
          return skip(game.id, 'division-required-for-placeholder-team')
        }
        patch.schedule_id = divisionId
      }

      return { gameId: game.id, patch }
    }

    case 'setOfficials': {
      const scope = OFFICIAL_BUCKET_TYPES[action.bucket]
      const desired = dedupeAssignments(action.assignments)

      /*
       * A role from another column cannot be written through this one. The
       * server rejects it outright — the scope is the contract — so catching it
       * here turns a 422 into a stated reason.
       */
      if (
        desired.some((assignment) => !scope.includes(assignment.officialTypeId))
      ) {
        return skip(game.id, 'official-role-outside-bucket')
      }

      //  The editor always commits a fresh array, so AG Grid reports a change
      //  even when Apply was pressed on an untouched selection.
      if (sameAssignments(assignmentsForBucket(game, action.bucket), desired)) {
        return skip(game.id, 'no-change')
      }

      return {
        gameId: game.id,
        patch: {
          officials: desired.map((assignment) => ({
            id: assignment.officialId,
            official_type_id: assignment.officialTypeId,
          })),
          officials_scope: scope,
        },
      }
    }

    default:
      return skip(game.id, 'no-change')
  }
}

/**
 * Turns a toolbar action plus a selection into concrete patches.
 *
 * Pure: no network, no clock reads, no React. Everything the UI needs to preview
 * an Apply — how many rows change, how many are skipped and why — comes out of
 * this one call, so the preview and the run can never disagree.
 */
export const planBulkEdit = (
  games: GameLike[],
  action: BulkAction,
  context: BulkPlanContext
): BulkPlan => {
  const patches: PlannedPatch[] = []
  const skipped: PlannedSkip[] = []

  for (const game of games) {
    const outcome = planForGame(game, action, context)
    if ('reason' in outcome) {
      skipped.push(outcome)
    } else {
      patches.push(outcome)
    }
  }

  return { patches, skipped }
}

/**
 * `if_necessary_status` rides along on every patch.
 *
 * The API used to assign it unconditionally, so a patch that omitted it cleared
 * the flag. That is fixed server-side, but a client can meet an older backend
 * during a deploy, and re-sending the value the row already has is a no-op
 * against the fixed one.
 */
export const withCompatibilityFields = (
  game: GameLike,
  patch: GamePatch
): GamePatch => ({
  ...patch,
  if_necessary_status: Boolean(game.if_necessary_status),
})

/**
 * Applies a patch to a game shape, for optimistic cache patching.
 * Kept here so the preview, the request and the cache all move in lockstep.
 */
export const applyPatchToGame = (
  game: GameLike,
  patch: GamePatch,
  context: BulkPlanContext
): GameLike => {
  const next: GameLike = { ...game }

  if (patch.starts_at !== undefined) {
    next.starts_at = patch.starts_at
  }
  if (patch.ends_at !== undefined) {
    next.ends_at = patch.ends_at
  }
  if (patch.league_game_number !== undefined) {
    next.league_game_number = patch.league_game_number
  }
  if (patch.game_type_id !== undefined) {
    next.game_type_id = patch.game_type_id
  }
  if (patch.venue_id !== undefined) {
    const venue = context.venuesById.get(patch.venue_id)
    if (venue !== undefined) {
      next.venue = venue
      //  Moving venue without naming a surface leaves the game unassigned
      //  rather than silently keeping the old venue's surface.
      next.facility = null
    }
  }
  if (patch.facility_id === null) {
    next.facility = null
  } else if (patch.facility_id !== undefined) {
    const venueId = patch.venue_id ?? next.venue?.id ?? ''
    const venue = context.venuesById.get(venueId)
    const facility = (venue?.facilities ?? []).find(
      (candidate) => candidate.id === patch.facility_id
    )
    next.facility = facility ?? next.facility ?? null
  }

  /*
   * The slot is deliberately LEFT ALONE. `sideLabel` already prefers a real
   * team over the slot it was generated from, so replacing the team is enough
   * for the cell to read correctly — and the API does not clear
   * `home_team_slot_id` either, so clearing it here would only make the
   * optimistic row disagree with the one the server will send back.
   *
   * An unknown uid still writes a bare `{ id }`: the row then shows nothing
   * useful for an instant rather than silently keeping the OLD team, which
   * would read as an edit that did not take.
   */
  if (patch.home_team_id !== undefined) {
    next.homeTeam = context.teamsById.get(patch.home_team_id) ?? {
      id: patch.home_team_id,
    }
  }
  if (patch.visiting_team_id !== undefined) {
    next.visitingTeam = context.teamsById.get(patch.visiting_team_id) ?? {
      id: patch.visiting_team_id,
    }
  }

  if (patch.officials !== undefined) {
    const scope = patch.officials_scope ?? null
    const existing = game.officials ?? []

    /*
     * Out-of-scope officials are carried across untouched — that is the whole
     * point of the scope. An unscoped patch replaces the lot.
     *
     * The rebuilt entries carry no name: the columns resolve names through the
     * options map, not through the row, so a name here would be a second source
     * of truth that only the optimistic path ever populated. Where the person is
     * already on the game their existing entry is reused, so nothing that WAS
     * named becomes unnamed.
     */
    const retained =
      scope === null
        ? []
        : existing.filter(
            (official) => !scope.includes(Number(official.official_type_id))
          )

    const applied = patch.officials.map((official) => {
      const known = existing.find((candidate) => candidate.id === official.id)
      return {
        ...(known ?? {}),
        id: official.id,
        official_type_id: official.official_type_id,
      }
    })

    next.officials = [...retained, ...applied]
  }

  return next
}

/** Local wall clock of a game, for rendering an editable cell. */
export const gameLocalParts = (game: GameLike) =>
  toVenueLocalParts(game.starts_at, game.venue?.timezone)
